import * as React from "react";
import { cn } from "@/lib/utils";
import { SectionLabel } from "./GradientText";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";

interface BenefitItem {
  title: string;
  description: string;
}

interface BenefitsProps {
  title: string;
  subtitle?: string;
  items: BenefitItem[];
}

/**
 * Benefits (Neo-Brutalist Version)
 *
 * Two-column layout with:
 * - Left: Sticky headline, subtitle, and CTA
 * - Right: Stacked benefit cards with green checkmark icons
 * - Hard shadows and bold borders
 */
export function Benefits({ title, subtitle, items }: BenefitsProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32 bg-[#FFFBF0]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left column - Sticky header */}
          <div
            className={cn(
              "lg:sticky lg:top-32",
              "transition-all duration-500 ease-out",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            )}
          >
            <SectionLabel color="green" className="mb-6">
              Benefits
            </SectionLabel>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4 font-['Epilogue'] text-[#1A1A1A]">
              {title}
            </h2>

            {subtitle && (
              <p className="text-[#666666] text-base lg:text-lg mb-8 font-['Sora']">
                {subtitle}
              </p>
            )}

            <Link to="/signup">
              <Button variant="success" size="lg">
                See all benefits
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Right column - Benefit cards */}
          <div className="space-y-4">
            {items.map((item, index) => (
              <div
                key={item.title}
                className={cn(
                  "group",
                  "transition-all duration-500 ease-out",
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                )}
                style={{
                  transitionDelay: isVisible ? `${(index + 1) * 100}ms` : "0ms",
                }}
              >
                {/* Card */}
                <div
                  className={cn(
                    "rounded-2xl overflow-hidden",
                    "bg-white",
                    "border-3 border-black",
                    "shadow-[6px_6px_0_0_#000000]",
                    "p-6",
                    "transition-all duration-200 ease-out",
                    "hover:-translate-x-1 hover:-translate-y-1",
                    "hover:shadow-[8px_8px_0_0_#00EB90]"
                  )}
                >
                  <div className="flex gap-4">
                    {/* Checkmark icon */}
                    <div
                      className={cn(
                        "w-12 h-12 rounded-xl shrink-0",
                        "bg-[#00EB90]",
                        "border-3 border-black",
                        "shadow-[3px_3px_0_0_#000000]",
                        "flex items-center justify-center",
                        "transition-all duration-200",
                        "group-hover:shadow-[4px_4px_0_0_#000000]"
                      )}
                    >
                      <Check className="w-6 h-6 text-black" strokeWidth={3} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-[#1A1A1A] mb-1 font-['Epilogue']">
                        {item.title}
                      </h3>
                      <p className="text-[#666666] text-sm leading-relaxed font-['Sora']">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
